import translator from "@/translator";
import React, { useEffect, useState } from "react";
import { List, Input, Button, Avatar, Typography, Spin, Space, Checkbox, message } from "antd";
import { PlusOutlined, MessageOutlined } from "@ant-design/icons";
import Search from "@/note_office/icons/Search";
import DropdownMenu from "./components/DropdownMenu";
import EmptyView from "@/note_office/shared/EmptyView";
import RedirectButton from "@/note_office/shared/RedirectButton";
import CustomPagination from "@/note_office/shared/CustomPagination";
import axios from "axios";
import Helper from "@/helper";
import useBulkUtils from "@/note_office/hooks/useBulkUtils";

const { Title } = Typography;
const t = translator("note_office.views.contacts.index_view.");

const IndexView = ({ contactsData }) => {
  const isDeleteMode = new URLSearchParams(window.location.search).get("mode") === "delete";
  const { cancelBulkMode, toggleBulkSelected } = useBulkUtils();
  const handleToggle = (id) => setSelectedIds(toggleBulkSelected(selectedIds, id));
  const handleCancel = cancelBulkMode;

  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState(contactsData.contacts || []);
  const [currentPage, setCurrentPage] = useState(contactsData.current_page || 1);
  const [loading, setLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(contactsData.total_pages || 0);
  const [totalContacts, setTotalContacts] = useState(contactsData.total_contacts || 0);
  const csrfToken = Helper.getCSRFToken();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const isSelected = (id) => selectedIds.has(id);

  const {
    per_page = 8,
    contacts_json_url,
    new_contact_url,
    import_url,
    dialogs_url,
    chatter_conversations_url,
    contacts_delete_url,
  } = contactsData;

  useEffect(() => {
    fetchContacts(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchContacts(1);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  const fetchContacts = async (page) => {
    setLoading(true);

    try {
      const params = { page, per_page };

      if (search.trim()) {
        params.query = search.trim();
      }

      const response = await axios.get(contacts_json_url, { params });

      setContacts(response.data.contacts || []);
      setTotalPage(response.data.total_pages);
      setTotalContacts(response.data.total_contacts);
    } catch (error) {
      console.error(t("errors.server_error"), error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const chatterButton = (event, contact) => {
    event.stopPropagation();
    const contactPhone = contact.phone.replace("+", "");

    window.location.href = `${dialogs_url}?phone=${contactPhone}`;
  };

  const onDelete = async () => {
    if (!confirm(t("bulk_destroy.confirm"))) return;

    try {
      const response = await axios.delete(contacts_delete_url, {
        data: { ids: Array.from(selectedIds) },
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
      });

      if (response.data.count) {
        const count = response.data.count;
        message.success(t("bulk_destroy.success", { count }));
      }

      if (response.data.errors) {
        message.error(response.data.errors);
      }

      setSelectedIds(new Set());
      const result = await axios.get(contacts_json_url, {
        params: { page: 1, per_page },
      });

      setContacts(result.data.contacts || []);
      setTotalPage(result.data.total_pages);
      setTotalContacts(result.data.total_contacts);

      if (result.data.total_contacts === 0) {
        handleCancel();
      }
    } catch (e) {
      message.error(e);
      console.error(e);
    }
  };

  return (
    <div className="wrapper wrapper__menu-layout overflow">
      {!totalContacts && !isDeleteMode ? (
        <EmptyView new_contact_url={new_contact_url} import_url={import_url} />
      ) : (
        <>
          <div className="contacts-index__header">
            {isDeleteMode ? (
              <Title level={4} className="contacts-index__title">
                {t("contacts.header_delete")}
              </Title>
            ) : (
              <Title level={4} className="contacts-index__title">
                {t("contacts.header")} <span style={{ color: "#EA9308" }}>{totalContacts}</span>{" "}
                {t("contacts", { count: totalContacts })} {t("contacts.header_tail")}
              </Title>
            )}
            <DropdownMenu contactsData={contactsData} />
          </div>

          <Input
            placeholder={t("placeholders.search_input")}
            prefix={<Search />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="contacts-index__search"
          />

          <div className="contacts-index__list overflow">
            <List
              itemLayout="horizontal"
              dataSource={contacts}
              locale={{
                emptyText: t("errors.no_contacts_found"),
              }}
              renderItem={(contact) => (
                <List.Item
                  onClick={() => {
                    if (!isDeleteMode) window.location.href = contact.show_url;
                    else handleToggle(contact.id);
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <div className="contacts-index__avatar-wrapper">
                        {isDeleteMode && (
                          <Checkbox
                            checked={isSelected(contact.id)}
                            onChange={() => handleToggle(contact.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <Avatar
                          className="contacts-index__avatar"
                          style={{ backgroundColor: contact.color }}
                        >
                          {contact.name[0].toUpperCase()}
                        </Avatar>
                      </div>
                    }
                    title={<div className="contacts-index__name">{contact.name}</div>}
                    description={<div className="contacts-index__phone">{contact.phone}</div>}
                  />
                  {!isDeleteMode && (
                    <Button
                      shape="circle"
                      icon={<MessageOutlined className="contacts-index__chat-icon" />}
                      onClick={(event) => chatterButton(event, contact)}
                      className="contacts-index__chat-btn"
                    />
                  )}
                </List.Item>
              )}
            />
            {loading && <Spin size="large" className="loader" />}
          </div>
        </>
      )}
      {totalPage > 1 && (
        <CustomPagination
          current={currentPage}
          total={totalContacts}
          pageSize={per_page}
          onChange={handlePageChange}
        />
      )}

      {!isDeleteMode && (
        <div className="contacts-index__add-btn">
          <RedirectButton
            url={new_contact_url}
            type="primary"
            shape="circle"
            icon={<PlusOutlined style={{ color: "white" }} />}
            size="large"
          />
        </div>
      )}

      {isDeleteMode && (
        <Space direction="vertical" size={16} className="button-group">
          <Button
            type="primary"
            className="button-base"
            block
            disabled={selectedIds.size === 0}
            onClick={onDelete}
          >
            {t("bulk_destroy.buttons.delete")}
          </Button>

          <Button
            type="default"
            block
            onClick={handleCancel}
            className="button-base button-secondary"
          >
            {t("bulk_destroy.buttons.cancel")}
          </Button>
        </Space>
      )}
    </div>
  );
};

export default IndexView;
